// src/Tools/claimTools.ts

import { FunctionDeclaration, Type } from "@google/genai";

//
// ═══════════════════════════════════════════════════════════
// CLAIM REQUEST FLOW TOOLS
// ═══════════════════════════════════════════════════════════
//

//
// ───────────────────────────────────────────────
//  1) REQUEST POLICY INQUIRY (SHOW INPUT POPUP)
// ───────────────────────────────────────────────
//

export const requestPolicyInquiryDeclaration: FunctionDeclaration = {
  name: "request_policy_inquiry",
  description:
    "Displays a popup to enter policy number. **Invocation Condition**: Call this IMMEDIATELY when the user wants to file a claim ('اضافة مطالبة'). Say 'برجاء ادخال رقم الوثيقة' then call this tool. Wait for 'system_ui user_submitted_policy_inquiry'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  2) SHOW CLAIM SURVEY OPTIONS (TWO BUTTONS)
// ───────────────────────────────────────────────
//

export const showClaimSurveyOptionsDeclaration: FunctionDeclaration = {
  name: "show_claim_survey_options",
  description:
    "Displays options: Live Survey vs Upload Images. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'تحب المعاينة لايف ولا رفع صور؟' then call this tool. Wait for 'system_ui user_selected_live_survey' or 'system_ui user_selected_upload_images'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  3) REQUEST CLAIM VEHICLE IMAGES (UPLOAD 4 IMAGES)
// ───────────────────────────────────────────────
//

export const requestClaimVehicleImagesDeclaration: FunctionDeclaration = {
  name: "request_claim_vehicle_images",
  description:
    "Displays UI to upload 4 vehicle images. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'من فضلك ارفع 4 صور' then call this tool. Wait for 'system_ui user_uploaded_claim_vehicle_images'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  4) SUBMIT CLAIM SURVEY (CALL BACKEND)
// ───────────────────────────────────────────────
//

//
// ───────────────────────────────────────────────
//  5) SHOW WORKSHOP OPTIONS (TWO BUTTONS)
// ───────────────────────────────────────────────
//

export const showWorkshopOptionsDeclaration: FunctionDeclaration = {
  name: "show_workshop_options",
  description:
    "Displays workshop options: Contracted vs Non-Contracted. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'تحب تصلح فين؟' then call this tool. Wait for 'system_ui user_selected_contracted_workshop' or 'system_ui user_selected_non_contracted_workshop'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  6) SHOW CONTRACTED WORKSHOPS LIST
// ───────────────────────────────────────────────
//

export const showContractedWorkshopsListDeclaration: FunctionDeclaration = {
  name: "show_contracted_workshops_list",
  description:
    "Fetches and displays contracted workshops. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'ثانية واحدة أراجع النظام… هجيب القائمة' then call this tool. Wait for 'system_ui user_chose_contracted_workshop'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  7) SHOW NON-CONTRACTED WORKSHOP FORM
// ───────────────────────────────────────────────
//

export const showNonContractedWorkshopFormDeclaration: FunctionDeclaration = {
  name: "show_non_contracted_workshop_form",
  description:
    "Displays form for non-contracted workshop. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'دخل بيانات المركز' then call this tool. Wait for 'system_ui user_submitted_non_contracted_workshop'.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  8) ANALYZE CLAIM VEHICLE IMAGES (CLAIM FLOW ONLY)
// ───────────────────────────────────────────────
//

export const analyzeClaimVehicleImagesDeclaration: FunctionDeclaration = {
  name: "analyze_claim_vehicle_images",
  description:
    "Analyzes vehicle images for CLAIM flow using Gemini AI to generate a detailed damage description in formal Egyptian Arabic. **Invocation Condition**: Call this when explicitly instructed by the system (Claim Flow only). You MUST say 'ثانية واحدة هحلل صور العربيه' immediately before calling this tool. This tool processes the 4 captured/uploaded images and returns a professional Arabic description of the vehicle's condition and damages. **CRITICAL**: After receiving the description response, read it naturally to the user in Egyptian Arabic. After you finish reading and audio playback completes, STOP. DO NOT call any other tools. Wait for system signal.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  9) SUBMIT CLAIM SURVEY REQUEST (VEHICLE SURVEY - CLAIM FLOW ONLY)
// ───────────────────────────────────────────────
//

export const submitClaimSurveyRequestDeclaration: FunctionDeclaration = {
  name: "submit_claim_survey_request",
  description:
    "Submits the vehicle survey data for CLAIM flow. **CRITICAL**: Call this function EXACTLY ONCE. **DO NOT** call it multiple times. **Invocation Condition**: Call this when explicitly instructed by the system (Claim Flow only, not Policy flow). Provide a description of damages found. Say 'هجمع البيانات وهبعتها للنظام…' before calling. After successful submission, wait for system instruction on what to do next.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      damage_description: {
        type: Type.STRING,
        description: "Description of vehicle condition and damages.",
      },
    },
    required: ["damage_description"],
  },
};

//
// ───────────────────────────────────────────────
//  10) SHOW CLAIM NUMBER
// ───────────────────────────────────────────────
//

export const showClaimNumberDeclaration: FunctionDeclaration = {
  name: "show_claim_number",
  description:
    "Displays the final claim number. **Invocation Condition**: Call this when explicitly instructed by the system. Say 'ده رقم المطالبة' then call this tool.",
  parameters: {
    type: Type.OBJECT,
    properties: {},
  },
};

//
// ───────────────────────────────────────────────
//  EXPORT ALL CLAIM TOOLS AS ARRAY
// ───────────────────────────────────────────────
//

export const ClaimTools: FunctionDeclaration[] = [
  requestPolicyInquiryDeclaration,
  showClaimSurveyOptionsDeclaration,
  requestClaimVehicleImagesDeclaration,
  analyzeClaimVehicleImagesDeclaration,
  submitClaimSurveyRequestDeclaration,
  showWorkshopOptionsDeclaration,
  showContractedWorkshopsListDeclaration,
  showNonContractedWorkshopFormDeclaration,
  showClaimNumberDeclaration,
];
